import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, MapPin, Calendar, TrendingUp, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KERALA_DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha', 'Kottayam',
  'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad', 'Malappuram',
  'Kozhikode', 'Wayanad', 'Kannur', 'Kasaragod'
];

export default function NewLaunches() {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    builder_name: searchParams.get('builder') || '',
    district: searchParams.get('district') || '',
    status: searchParams.get('status') || ''
  });

  useEffect(() => {
    fetchProjects();
  }, [filters]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.builder_name) params.append('builder_name', filters.builder_name);
      if (filters.district) params.append('district', filters.district);
      if (filters.status) params.append('status', filters.status);
      params.append('approved_only', 'true');
      
      const response = await axios.get(`${API}/builder-projects?${params.toString()}`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching builder projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const uniqueBuilders = [...new Set(projects.map(p => p.builder_name))];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <div className="bg-primary text-white py-12">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-2" data-testid="new-launches-heading">
              New Launches in Kerala
            </h1>
            <p className="text-base text-white/90">
              Discover the latest residential projects from Kerala's top builders
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-10">
        {/* Filters */}
        <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-100 mb-6" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-primary" />
            <h3 className="text-base font-semibold">Filter Projects</h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Builder</label>
              <Select
                value={filters.builder_name}
                onValueChange={(value) => setFilters({ ...filters, builder_name: value })}
              >
                <SelectTrigger data-testid="filter-builder">
                  <SelectValue placeholder="All Builders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Builders</SelectItem>
                  {uniqueBuilders.map((builder) => (
                    <SelectItem key={builder} value={builder}>
                      {builder}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">District</label>
              <Select
                value={filters.district}
                onValueChange={(value) => setFilters({ ...filters, district: value })}
              >
                <SelectTrigger data-testid="filter-district">
                  <SelectValue placeholder="All Districts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Districts</SelectItem>
                  {KERALA_DISTRICTS.map((district) => (
                    <SelectItem key={district} value={district}>
                      {district}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="open for booking">Open for Booking</SelectItem>
                  <SelectItem value="sold out">Sold Out</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-projects">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading new launches...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-100" data-testid="no-projects">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">No projects found</p>
            <p className="text-slate-500 text-sm mt-2">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
            {projects.map((project) => (
              <div
                key={project.project_id}
                data-testid={`project-card-${project.project_id}`}
                className="group bg-white rounded-lg overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.images?.[0] || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00'}
                    alt={project.project_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-primary text-white text-xs font-medium rounded">
                      {project.builder_name}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-0.5 text-white text-xs font-medium rounded ${
                      project.status === 'open for booking' ? 'bg-green-500' :
                      project.status === 'upcoming' ? 'bg-blue-500' : 'bg-slate-500'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="text-base font-semibold mb-1.5 line-clamp-1">{project.project_name}</h3>
                  
                  <div className="flex items-center text-slate-600 mb-2">
                    <MapPin className="w-3.5 h-3.5 mr-1" />
                    <span className="text-sm">{project.location?.city || project.location?.district}, Kerala</span>
                  </div>

                  {project.configurations && project.configurations.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {project.configurations.slice(0, 3).map((config, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                          {config}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-500">Price Range</p>
                      <p className="text-base font-bold text-primary">
                        ₹{(project.price_range?.min / 10000000).toFixed(1)}Cr+
                      </p>
                    </div>
                    {project.launch_date && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Launch</p>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-secondary" />
                          <p className="text-xs font-medium text-secondary">
                            {new Date(project.launch_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {project.rera_number && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500">RERA: {project.rera_number}</p>
                    </div>
                  )}

                  <Button
                    data-testid={`view-project-${project.project_id}`}
                    className="w-full mt-3 bg-primary hover:bg-primary/90 text-white text-sm"
                    onClick={() => window.open(project.source_url, '_blank')}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Section */}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <div className="bg-white p-5 rounded-lg border border-slate-100 text-center">
            <Building2 className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-primary mb-0.5">{projects.length}</p>
            <p className="text-sm text-slate-600">Active Projects</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-slate-100 text-center">
            <TrendingUp className="w-6 h-6 text-secondary mx-auto mb-2" />
            <p className="text-2xl font-bold text-secondary mb-0.5">{uniqueBuilders.length}</p>
            <p className="text-sm text-slate-600">Trusted Builders</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-slate-100 text-center">
            <MapPin className="w-6 h-6 text-accent mx-auto mb-2" />
            <p className="text-2xl font-bold text-accent mb-0.5">{[...new Set(projects.map(p => p.location?.district))].length}</p>
            <p className="text-sm text-slate-600">Districts Covered</p>
          </div>
        </div>
      </div>
    </div>
  );
}